// =============================================================================
// AdminFailedPaymentsPage — wholesale settlement holds / failed payments
// =============================================================================
// Operator surface for orders whose WHOLESALE settlement needs attention
// (wholesaleChargeStatus ON_HOLD / FAILED). These orders are pre-production —
// the IN_PRODUCTION gate blocks them until PAID — so an operator can either:
//   • Retry the charge (after the merchant tops up / fixes their card), or
//   • Cancel the held order (releases any reserved wallet hold + notifies).
//
// Read is gated by `order:list`; mutating actions require `order:manage`.
// =============================================================================

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, WholesaleChargeStatus } from '@tkpod/shared';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Spinner,
  useConfirm,
  type BadgeVariant,
} from '@tkpod/ui';

import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';

// ---------------------------------------------------------------------------
// Types — mirror PaymentHoldRow / PaymentHoldsListResult on the API
// ---------------------------------------------------------------------------

interface PaymentHoldRow {
  id: string;
  orderNumber: string | null;
  organizationId: string;
  organizationName: string | null;
  wholesaleChargeStatus: string;
  chargeHoldReason: string | null;
  chargeHeldAt: string | null;
  lastChargeFailureCode: string | null;
  chargeAttemptCount: number;
  cardChargeAttempts: number;
  nextChargeRetryAt: string | null;
  wholesaleTotalCostCents: string;
  currency: string;
  status: string;
  createdAt: string;
}

interface ListResponse {
  items: PaymentHoldRow[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<string, BadgeVariant> = {
  ON_HOLD: 'danger',
  FAILED: 'danger',
  PENDING: 'warning',
  CHARGING: 'info',
  PAID: 'success',
  NONE: 'default',
};

const STATUS_LABEL: Record<string, string> = {
  ON_HOLD: 'On hold',
  FAILED: 'Failed',
  PENDING: 'Pending',
  CHARGING: 'Charging',
  PAID: 'Paid',
  NONE: 'No charge',
};

const HOLD_REASON_LABEL: Record<string, string> = {
  NO_PAYMENT_METHOD: 'No card on file',
  CARD_DECLINED: 'Card declined',
  SCA_REQUIRED: 'Authentication required (SCA)',
  MANUAL_REVIEW: 'Manual review',
  DUNNING_EXHAUSTED: 'Dunning exhausted',
  WALLET_FROZEN: 'Wallet frozen',
  CARD_PENDING: 'Card processing',
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All active (needs attention)' },
  { value: WholesaleChargeStatus.ON_HOLD, label: 'On hold' },
  { value: WholesaleChargeStatus.FAILED, label: 'Failed' },
  { value: WholesaleChargeStatus.PENDING, label: 'Pending' },
  { value: WholesaleChargeStatus.CHARGING, label: 'Charging' },
];

function formatCents(cents: string, currency: string): string {
  const amount = Number(cents) / 100;
  if (!Number.isFinite(amount)) return `${cents} ${currency}`;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function reasonLabel(row: PaymentHoldRow): string {
  const code = row.chargeHoldReason ?? row.lastChargeFailureCode;
  if (!code) return '—';
  return HOLD_REASON_LABEL[code] ?? code;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminFailedPaymentsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const permissions = user?.permissions ?? user?.currentOrganization?.permissions ?? [];
  const canManage = hasPermission(permissions, 'order:manage');

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const listQuery = useQuery<ListResponse, Error>({
    queryKey: ['admin', 'payment-holds', { statusFilter, search, page }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      return api.get<ListResponse>(
        `/api/v1/admin/orders/payment-holds?${params.toString()}`,
      );
    },
    refetchInterval: 30_000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'payment-holds'] });
  }, [queryClient]);

  const retryMutation = useMutation<unknown, Error, string>({
    mutationFn: (orderId) =>
      api.post(`/api/v1/admin/orders/${orderId}/charge`, {
        reason: 'Admin manual retry from failed-payments view',
      }),
    onSuccess: () => {
      toast('Charge retried — refreshing.', 'success');
      invalidate();
    },
    onError: (err) => toast(err.message || 'Retry failed.', 'error'),
  });

  const cancelMutation = useMutation<unknown, Error, string>({
    mutationFn: (orderId) =>
      api.post(`/api/v1/admin/orders/${orderId}/cancel-hold`, {
        reason: 'Admin manual cancel from failed-payments view',
      }),
    onSuccess: () => {
      toast('Order cancelled.', 'success');
      invalidate();
    },
    onError: (err) => toast(err.message || 'Cancel failed.', 'error'),
  });

  const applySearch = useCallback(() => {
    setPage(1);
    setSearch(searchInput.trim());
  }, [searchInput]);

  const onRetry = useCallback(
    async (row: PaymentHoldRow) => {
      const ok = await confirm({
        title: 'Retry wholesale charge',
        description: `Re-attempt collecting ${formatCents(
          row.wholesaleTotalCostCents,
          row.currency,
        )} for order ${row.orderNumber ?? row.id}. Safe to run repeatedly.`,
        confirmLabel: 'Retry charge',
      });
      if (ok) retryMutation.mutate(row.id);
    },
    [confirm, retryMutation],
  );

  const onCancel = useCallback(
    async (row: PaymentHoldRow) => {
      const ok = await confirm({
        title: 'Cancel held order',
        description: `Cancel order ${
          row.orderNumber ?? row.id
        } and release any reserved wallet hold. The merchant is notified. This cannot be undone.`,
        confirmLabel: 'Cancel order',
        tone: 'danger',
      });
      if (ok) cancelMutation.mutate(row.id);
    },
    [confirm, cancelMutation],
  );

  const rows = listQuery.data?.items ?? [];
  const meta = listQuery.data?.meta;
  const busyId =
    retryMutation.isPending
      ? retryMutation.variables
      : cancelMutation.isPending
        ? cancelMutation.variables
        : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text-primary">Failed payments</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Orders whose wholesale settlement still needs attention — on hold,
          failed, pending, or mid-charge. These never enter production until the
          charge is collected. Retry after the merchant tops up, or cancel a
          held order to release it.
        </p>
      </div>

      <Card>
        <CardHeader
          title="Settlement holds"
          description={meta ? `${meta.total} order(s) need attention.` : undefined}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="Search order #"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
                className="w-44"
              />
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                options={STATUS_FILTER_OPTIONS}
                className="w-56"
              />
            </div>
          }
        />

        {listQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : listQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-sm font-medium text-danger-600">
              Failed to load payment holds.
            </p>
            <Button variant="secondary" onClick={() => listQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-text-secondary">
              No orders need attention. 🎉
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border-subtle text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-text-secondary">
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Settlement</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Attempts</th>
                  <th className="px-4 py-3">Held since</th>
                  {canManage && <th className="px-4 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {rows.map((row) => (
                  <tr key={row.id} className="align-middle">
                    <td className="px-4 py-3 font-medium text-text-primary">
                      {row.orderNumber ?? row.id}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {row.organizationName ?? row.organizationId}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={STATUS_BADGE[row.wholesaleChargeStatus] ?? 'default'}
                        size="sm"
                        dot
                      >
                        {STATUS_LABEL[row.wholesaleChargeStatus] ??
                          row.wholesaleChargeStatus}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {reasonLabel(row)}
                    </td>
                    <td className="px-4 py-3 text-text-primary">
                      {formatCents(row.wholesaleTotalCostCents, row.currency)}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {row.chargeAttemptCount}
                      {row.cardChargeAttempts > 0
                        ? ` (${row.cardChargeAttempts} card)`
                        : ''}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {formatDate(row.chargeHeldAt)}
                    </td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={busyId === row.id}
                            onClick={() => onRetry(row)}
                          >
                            Retry
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={
                              busyId === row.id ||
                              row.wholesaleChargeStatus !==
                                WholesaleChargeStatus.ON_HOLD
                            }
                            onClick={() => onCancel(row)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border-subtle px-4 py-3">
            <span className="text-xs text-text-secondary">
              Page {meta.page} of {meta.totalPages} · {meta.total} total
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {confirmDialog}
    </div>
  );
}
